import React, { useState, FormEvent, FocusEvent, useContext } from 'react';
import Draggable, { DraggableEventHandler } from 'react-draggable';
import { ButtonColor, ButtonVariant, ButtonSize } from 'react-magma-dom';
import { UndoRedoContext } from '../../contexts/undoRedoContext';
import { getProportion, getPixelsFromPercents, getCursor } from '../../utils';
import { useMainContentSize } from '../../hooks/useMainContentSize';
import { FlexColumnWrapper, StyledNoteWrapper, AbsoluteNoteWrapper, StyledNote, DivAsButtonWrapper } from '../Styled';
import { PARENT_ELEMENT, NOTE_TYPE, OnContentChange, dragStart } from '../../utils/draggable';
import { CloseIcon } from '../../assets/icons/DraggableNote';
import { IconButtonStyled } from '../Styled/StyledButtons';
import { IPosition } from '../../types';
import { isUndoButtonClicked, isUndoOrRedoButtonClicked } from '../../utils/undoRedo';

interface IDraggableNote {
  id: string;
  editMode: boolean;
  isFocussed: boolean;
  isManuallyHidden: boolean;
  selectionStart: number;
  content: string;
  position: IPosition;
  colors: {
    primary: string;
    dark: string;
  };
  handleStop: (id: string, position: IPosition, content: string) => void;
  handleRemove: (id: string) => void;
  handleBlur: (id: string, content: string, selectionStart: number) => void;
}

export const ROWS_COUNT = 15;
export const COLS_COUNT = 20;

export const DraggableNote: React.FC<IDraggableNote> = (props): JSX.Element => {
  const {
    id,
    isFocussed,
    isManuallyHidden,
    selectionStart,
    editMode,
    content,
    position,
    colors,
    handleStop,
    handleRemove,
    handleBlur,
  } = props;

  const { undo, redo } = useContext(UndoRedoContext);

  const [bounds, setBounds] = useState({ left: 0, right: 0, top: 0, bottom: 0 });
  const [isInMove, setMove] = useState(false);
  const [text, setContent] = useState(content);
  const [x, y] = useMainContentSize();

  const handleStart: DraggableEventHandler = (_, data) => {
    const { node } = data;
    dragStart(data, PARENT_ELEMENT, setBounds);
    node.style.zIndex = '41';
  };

  const handleDragStop: DraggableEventHandler = (_, data) => {
    const { node } = data;
    const child = node?.firstChild?.lastChild as HTMLTextAreaElement;

    const value = child.value;

    const newPosition = {
      x: getProportion(data.x, x),
      y: getProportion(data.y, y),
    };

    const isPositionChanged = JSON.stringify(position) !== JSON.stringify(newPosition);

    if (!isPositionChanged) {
      return;
    }

    setContent(value);
    handleStop(id, newPosition, value);

    node.style.zIndex = '41';
    return false;
  };

  const handleNoteBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
    const isEmptyValue = event.target.value.trim() === '';

    if (isUndoOrRedoButtonClicked(event)) {
      isUndoButtonClicked(event) ? undo() : redo();
    } else if (isEmptyValue) {
      handleRemove(id);
    } else if (content !== event.target.value) {
      handleBlur(id, text, text.length);
    }

    const target = event.target as HTMLTextAreaElement;
    const parent = target.parentElement?.parentElement as HTMLElement;
    parent.style.zIndex = '41';
  };

  const handleChange = (event: FocusEvent<HTMLTextAreaElement>) => {
    OnContentChange(event, setContent, NOTE_TYPE);
  };

  const handleFocus = (event: FormEvent<HTMLTextAreaElement>) => {
    const target = event.target as HTMLTextAreaElement;
    const parent = target.parentElement?.parentElement as HTMLElement;
    parent.style.zIndex = '42';
    target.selectionStart = selectionStart;
  };

  return (
    <Draggable
      handle=".handle"
      onStart={handleStart}
      onStop={handleDragStop}
      onDrag={() => setMove(true)}
      bounds={bounds}
      positionOffset={{ x: '-50%', y: '-50%' }}
      position={{ x: getPixelsFromPercents(x, position.x), y: getPixelsFromPercents(y, position.y) }}
    >
      <AbsoluteNoteWrapper isManuallyHidden={isManuallyHidden}>
        <FlexColumnWrapper>
          <StyledNoteWrapper data-testid="note-heading" bgColor={colors.primary}>
            <div data-testid="note" className="handle" style={{ width: '100%', cursor: getCursor(isInMove) }} />
            {editMode ? (
              <IconButtonStyled
                testId="remove-note"
                tabIndex={-1}
                onMouseUp={() => handleRemove(id)}
                aria-label="Remove note"
                variant={ButtonVariant.link}
                color={ButtonColor.secondary}
                bgColor={colors.dark}
                size={ButtonSize.small}
                icon={<CloseIcon />}
              />
            ) : (
              <DivAsButtonWrapper />
            )}
          </StyledNoteWrapper>
          <StyledNote
            data-testid="note-body"
            tabIndex={-1}
            autoCapitalize={'on'}
            rows={ROWS_COUNT}
            cols={COLS_COUNT}
            value={text}
            readOnly={!editMode}
            onFocus={handleFocus}
            onChange={handleChange}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={editMode && isFocussed}
            onBlur={handleNoteBlur}
            placeholder="Type note here"
          />
        </FlexColumnWrapper>
      </AbsoluteNoteWrapper>
    </Draggable>
  );
};
